<script setup lang="ts">
import LeDownArrow from "@dsk/icons/LeDownArrow.vue";
import LeNetwork from "@dsk/icons/LeNetwork.vue";
import LeProfile from "@dsk/icons/LeProfile.vue";
import { vOnClickOutside } from "@vueuse/components";
import { type Component, computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useHeaderMenuItems } from "@/composables";
import { UserType } from "@/types";

import LeHeaderMenu from "./LeHeaderMenu.vue";

interface Props {
  color?: "primary" | "white";
  companyName: string;
  username: string;
  userType: UserType;
  isPaying: boolean;
  isNetwork: boolean;
  isEnterpriseAdmin: boolean;
  isEnterpriseDictionaryEditor: boolean;
  networkName: string;
}

const props = withDefaults(defineProps<Props>(), { color: "white" });

const { t } = useI18n();

const buttonEl = ref<HTMLButtonElement | null>(null);

const isDropdownOpen = ref(false);
const currentFocusIndex = ref<number | null>(null);
watch(currentFocusIndex, (index) => {
  if (index !== null) {
    return;
  }

  buttonEl.value?.focus();
});

const onClick = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
  currentFocusIndex.value = null;
};
const onClose = () => {
  isDropdownOpen.value = false;
  currentFocusIndex.value = null;
};
const onClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;

  if (target.closest("button.greeting-container") !== null) {
    return;
  }

  onClose();
};

const { menuItems } = useHeaderMenuItems(
  props.userType,
  props.isPaying,
  props.isEnterpriseAdmin,
  props.isEnterpriseDictionaryEditor
);
const maxIndex = computed<number>(() => menuItems.value.length - 1);
const onArrowUp = () => {
  if (currentFocusIndex.value === null) {
    return;
  }

  if (currentFocusIndex.value === 0) {
    currentFocusIndex.value = null;
    return;
  }

  currentFocusIndex.value--;
};
const onArrowDown = () => {
  isDropdownOpen.value = true;

  if (currentFocusIndex.value === null) {
    currentFocusIndex.value = 0;
    return;
  }

  if (currentFocusIndex.value === maxIndex.value) {
    return;
  }

  currentFocusIndex.value++;
};

interface GreetingItem {
  loginType: {
    label: string;
    identifier: string;
  };
  icon: Component;
}
const greeting = computed<GreetingItem>(() => {
  let label = "";
  let identifier = "";
  let icon = LeProfile;

  switch (props.userType) {
    case UserType.Private:
    case UserType.Company:
      label = t("greeting.logged-in-as");
      identifier = props.username;
      break;
    case UserType.Unilogin:
      label = t("greeting.logged-in-via", { provider: t("providers.unilogin") });
      identifier = props.companyName;
      break;
    case UserType.Wayf:
      label = t("greeting.logged-in-with", { provider: "" });
      identifier = "WAYF";
      break;
    case UserType.OIDC:
      label = t("greeting.logged-in-with", { provider: "" });
      identifier = props.companyName;
      break;
    case UserType.Network:
    case UserType.SchoolNetwork:
      label = t("greeting.logged-in-via", { provider: t("providers.network") });
      identifier = props.networkName;
      icon = LeNetwork;
      break;
  }

  return {
    loginType: {
      label,
      identifier
    },
    icon
  };
});
</script>

<template>
  <button
    v-bind="$attrs"
    ref="buttonEl"
    :aria-label="t('label')"
    class="greeting-container"
    :class="{ 'greeting-container--open': isDropdownOpen, [`greeting-container--${color}`]: true }"
    @click="onClick()"
    @keydown.esc.prevent="onClose()"
    @keydown.up.prevent="onArrowUp()"
    @keydown.down.prevent="onArrowDown()"
  >
    <span class="greeting-container__inner">
      <div class="greeting-container__text d-none d-md-block">
        <li class="greeting-container__text--italic">{{ greeting.loginType.label }}</li>
        <li class="greeting-container__text--bold">{{ greeting.loginType.identifier }}</li>
      </div>
      <Component :is="greeting.icon" class="greeting-container__icon" />
      <LeDownArrow class="greeting-container__arrow" />
    </span>
  </button>
  <LeHeaderMenu
    v-if="isDropdownOpen"
    v-on-click-outside="onClickOutside"
    :network-name="props.networkName"
    :user-type="props.userType"
    :is-network="props.isNetwork"
    :is-paying="props.isPaying"
    :focus-index="currentFocusIndex"
    :aria-expanded="isDropdownOpen"
    :is-enterprise-admin="props.isEnterpriseAdmin"
    :is-enterprise-dictionary-editor="props.isEnterpriseDictionaryEditor"
    @keydown.esc.prevent="onClose()"
    @keydown.up.prevent="onArrowUp()"
    @keydown.down.prevent="onArrowDown()"
    @item-click="onClose()"
  />
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

.greeting-container {
  padding: 5px 10px;
  border: none;
  border-radius: 59px;
  background: none;

  @media screen and (max-width: 767px) {
    padding: 10px 15px;
  }

  &--white {
    color: white;
  }

  &--primary {
    color: @colors[@primary][500];
  }

  &:hover,
  &--open {
    background-color: @colors[@primary][800];

    &.greeting-container--white,
    &.greeting-container--primary {
      color: white;
    }
  }

  &.focus-visible,
  &:focus:not(.focus-visible) {
    .focus-outline(green);
  }

  &__inner {
    display: flex;
    align-items: center;
  }

  &__text {
    margin: 0 10px;
    text-align: right;

    &--italic {
      font-style: italic;
    }

    &--bold {
      max-width: 230px;
      overflow: hidden;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    li {
      font-size: 14px;
      list-style: none;
    }
  }

  &__icon {
    width: 29.76px;
    height: 29.76px;
    margin: 0 10px;
  }
}
</style>

<i18n lang="json">
{
  "da": {
    "label": "Åbn menu",
    "greeting": {
      "logged-in-as": "Logget ind som:",
      "logged-in-with": "Logget ind med:",
      "logged-in-via": "Logget ind via {provider}:"
    },
    "providers": {
      "unilogin": "Unilogin",
      "network": "Åbent netværk"
    }
  },
  "en": {
    "label": "Open menu",
    "greeting": {
      "logged-in-as": "Logged in as:",
      "logged-in-with": "Logged in with:",
      "logged-in-via": "Logged in via {provider}:"
    },
    "providers": {
      "unilogin": "Unilogin",
      "network": "Open Network"
    }
  },
  "de": {
    "label": "Menü öffnen",
    "greeting": {
      "logged-in-as": "Angemeldet als:",
      "logged-in-with": "Angemeldet mit:",
      "logged-in-via": "Angemeldet über {provider}:"
    },
    "providers": {
      "unilogin": "Unilogin",
      "network": "Offenes Netzwerk"
    }
  }
}
</i18n>
